import React, { useState, useEffect, useCallback } from "react";
import {
  Modal,
  Form,
  Input,
  Button,
  Space,
  Alert,
  Spin,
  Card,
  Descriptions,
  Divider,
  Tabs,
  message,
  Select,
  Switch,
  DatePicker,
  TimePicker,
  Segmented,
} from "antd";
import { apiRequests } from "src/shared/api/api";
import { SaveOutlined } from "@ant-design/icons";

const { TabPane } = Tabs;

const OWNERSHIP_OPTIONS = [
  { value: 9, label: "Акционерное общество" },
  { value: 61, label: "Закрытое акционерное общество" },
  { value: 63, label: "Индивидуальный предприниматель" },
  { value: 119, label: "Открытое акционерное общество" },
  { value: 137, label: "Общество с ограниченной ответственностью" },
  { value: 147, label: "Публичное акционерное общество" },
  { value: 179, label: "Товарищество с ограниченной ответственностью" },
];

const CdekModal = ({ visible, onClose, query }) => {
  const [activeTab, setActiveTab] = useState("lk");
  const [loading, setLoading] = useState(false);
  const [lkProfile, setLkProfile] = useState(null);
  const [lkForm] = Form.useForm();
  const [templateForm] = Form.useForm();

  const [orderType, setOrderType] = useState("im");
  const [recipientLegal, setRecipientLegal] = useState(false);
  const [pickupMode, setPickupMode] = useState("door");
  const [deliveryMode, setDeliveryMode] = useState("door");

  const [senderCityOptions, setSenderCityOptions] = useState([]);
  const [recipientCityOptions, setRecipientCityOptions] = useState([]);
  const [loadingSenderCity, setLoadingSenderCity] = useState(false);
  const [loadingRecipientCity, setLoadingRecipientCity] = useState(false);

  const [senderSearch, setSenderSearch] = useState("");
  const [recipientSearch, setRecipientSearch] = useState("");

  const [selectedSenderCity, setSelectedSenderCity] = useState(null);
  const [selectedRecipientCity, setSelectedRecipientCity] = useState(null);
  const [selectedPickupPvz, setSelectedPickupPvz] = useState(null);
  const [selectedDeliveryPoint, setSelectedDeliveryPoint] = useState(null);

  const [pickupPvzOptions, setPickupPvzOptions] = useState([]);
  const [deliveryPvzOptions, setDeliveryPvzOptions] = useState([]);
  const [loadingPickupPvz, setLoadingPickupPvz] = useState(false);
  const [loadingDeliveryPvz, setLoadingDeliveryPvz] = useState(false);

  const [pickupAddressOptions, setPickupAddressOptions] = useState([]);
  const [deliveryAddressOptions, setDeliveryAddressOptions] = useState([]);
  const [loadingPickupAddress, setLoadingPickupAddress] = useState(false);
  const [loadingDeliveryAddress, setLoadingDeliveryAddress] = useState(false);

  const [pickupAddressSearch, setPickupAddressSearch] = useState("");
  const [deliveryAddressSearch, setDeliveryAddressSearch] = useState("");

  const [loadingTemplate, setLoadingTemplate] = useState(false);

  const checkSession = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiRequests.integrationCdek.lkSession(query.token);
      if (res.data.has_session && res.data.profile) {
        setLkProfile(res.data.profile);
        setActiveTab("template");
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [query.token]);

  const loadTemplate = useCallback(async () => {
    setLoadingTemplate(true);
    try {
      const res = await apiRequests.integrationCdek.getTemplate(query.token);
      if (res.data) {
        templateForm.setFieldsValue(res.data);

        if (res.data.pickup_mode) setPickupMode(res.data.pickup_mode);
        if (res.data.delivery_mode) setDeliveryMode(res.data.delivery_mode);
        if (res.data.order_type) setOrderType(res.data.order_type);
        if (res.data.recipient_legal !== undefined) setRecipientLegal(res.data.recipient_legal);
        if (res.data.sender_city_data) setSelectedSenderCity(res.data.sender_city_data);
        if (res.data.recipient_city_data) setSelectedRecipientCity(res.data.recipient_city_data);
        if (res.data.pickup_pvz_data) setSelectedPickupPvz(res.data.pickup_pvz_data);
        if (res.data.delivery_point_data) setSelectedDeliveryPoint(res.data.delivery_point_data);
      }
    } catch (error) {
      console.error(error);
      message.error("Не удалось загрузить шаблон");
    } finally {
      setLoadingTemplate(false);
    }
  }, [query.token, templateForm]);

  const fetchSenderCities = useCallback(async (search) => {
    if (!search) return;
    setLoadingSenderCity(true);
    try {
      const res = await apiRequests.integrationCdek.suggestCities({ name: search }, query.token);
      const options = (res.data || []).map((city) => ({
        label: city.full_name,
        value: city.full_name,
        data: { ...city, full_name: city.full_name },
      }));
      setSenderCityOptions(options);
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingSenderCity(false);
    }
  }, [query.token]);

  const fetchRecipientCities = useCallback(async (search) => {
    if (!search) return;
    setLoadingRecipientCity(true);
    try {
      const res = await apiRequests.integrationCdek.suggestCities({ name: search }, query.token);
      const options = (res.data || []).map((city) => ({
        label: city.full_name,
        value: city.full_name,
        data: { ...city, full_name: city.full_name },
      }));
      setRecipientCityOptions(options);
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingRecipientCity(false);
    }
  }, [query.token]);

  const loadPickupPvz = useCallback(async (cityCode) => {
    if (!cityCode) return;
    setLoadingPickupPvz(true);
    try {
      const res = await apiRequests.integrationCdek.getDeliveryPoints({ city_code: cityCode }, query.token);
      const options = (res.data || []).map((point) => ({
        label: point.location?.address_full || `${point.location?.address} (${point.code})`,
        value: point.code,
        data: point,
      }));
      setPickupPvzOptions(options);
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingPickupPvz(false);
    }
  }, [query.token]);

  const loadDeliveryPvz = useCallback(async (cityCode) => {
    if (!cityCode) return;
    setLoadingDeliveryPvz(true);
    try {
      const res = await apiRequests.integrationCdek.getDeliveryPoints({ city_code: cityCode }, query.token);
      const options = (res.data || []).map((point) => ({
        label: point.location?.address_full || `${point.location?.address} (${point.code})`,
        value: point.code,
        data: point,
      }));
      setDeliveryPvzOptions(options);
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingDeliveryPvz(false);
    }
  }, [query.token]);

  const fetchPickupAddress = useCallback(async (value) => {
    if (!value || !selectedSenderCity?.uuid) return;
    setLoadingPickupAddress(true);
    const cityUuid = selectedSenderCity.uuid;
    if (!cityUuid) return;

    try {
      const res = await apiRequests.integrationCdek.addressSuggestions(
        { cityUuid, value },
        query.token
      );
      const options = (res.data?.items || []).map((addr) => ({
        label: addr.suggestion,
        value: addr.suggestion,
        data: addr,
      }));
      setPickupAddressOptions(options);
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingPickupAddress(false);
    }
  }, [selectedSenderCity, query.token]);

  const fetchDeliveryAddress = useCallback(async (value) => {
    if (!value || !selectedRecipientCity) return;
    const cityUuid = selectedRecipientCity.uuid || selectedRecipientCity.city_uuid;
    if (!cityUuid) return;

    setLoadingDeliveryAddress(true);
    try {
      const res = await apiRequests.integrationCdek.addressSuggestions(
        { cityUuid, value },
        query.token
      );
      const options = (res.data?.items || []).map((addr) => ({
        label: addr.suggestion,
        value: addr.suggestion,
        data: addr,
      }));
      setDeliveryAddressOptions(options);
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingDeliveryAddress(false);
    }
  }, [selectedRecipientCity, query.token]);

  

  useEffect(() => {
    if (visible && query?.token) checkSession();
  }, [visible, query?.token, checkSession]);

  useEffect(() => {
    if (!visible) {
      setActiveTab("lk");
      setLkProfile(null);
      lkForm.resetFields();
      templateForm.resetFields();

      setOrderType("im");
      setRecipientLegal(false);
      setPickupMode("door");
      setDeliveryMode("door");

      setSenderCityOptions([]);
      setRecipientCityOptions([]);
      setPickupPvzOptions([]);
      setDeliveryPvzOptions([]);
      setPickupAddressOptions([]);
      setDeliveryAddressOptions([]);

      setSelectedSenderCity(null);
      setSelectedRecipientCity(null);
      setSelectedPickupPvz(null);
      setSelectedDeliveryPoint(null);
    }
  }, [visible, lkForm, templateForm]);

  useEffect(() => {
    if (lkProfile) {
      const phone = lkProfile.phones?.[0]?.number || "";
      const companyName = lkProfile.displayName || "";
      const contactName = lkProfile.contactName || "";
      const inn = lkProfile.inn || "";

      const cityData = lkProfile.addressLegal?.city
        ? {
            code: lkProfile.addressLegal.city.code,
            uuid: lkProfile.addressLegal.city.uuid,
            full_name: lkProfile.addressLegal.city.fullName,
            ...lkProfile.addressLegal.city,
          }
        : null;

      templateForm.setFieldsValue({
        order_type: "im",
        sender_company: companyName,
        sender_fio: contactName,
        sender_phone: phone,
        sender_inn: inn,
        sender_city_data: cityData,

        im_number: "",
        seller_name: contactName,
        seller_ownership_form: lkProfile.ownershipTypeCode,
        seller_inn: inn,
        seller_phone: phone,

        recipient_legal: false,
        recipient_company: "",
        recipient_inn: "",
        recipient_fio: "",
        recipient_phone: "",
        recipient_city_data: null,
        recipient_email: "",

        pay_delivery_recipient: false,
        pay_bank_commission: false,

        pickup_mode: "door",
        delivery_mode: "door",

        pickup_address: "",
        pickup_address_data: null,
        pickup_date: null,
        pickup_time_from: null,
        pickup_time_to: null,
        pickup_break_from: null,
        pickup_break_to: null,
        pickup_need_authority: false,
        pickup_need_passport: false,
        pickup_comment: "",

        pickup_pvz_data: null,

        delivery_address: "",
        delivery_address_data: null,
        delivery_comment: "",

        delivery_point_data: null,
      });

      if (cityData) setSelectedSenderCity(cityData);
    }
  }, [lkProfile, templateForm]);

  useEffect(() => {
    if (activeTab === "template" && lkProfile && query?.token) {
      loadTemplate();
    }
  }, [activeTab, lkProfile, query?.token, loadTemplate]);

  useEffect(() => {
    const timer = setTimeout(() => senderSearch && fetchSenderCities(senderSearch), 300);
    return () => clearTimeout(timer);
  }, [senderSearch, fetchSenderCities]);

  useEffect(() => {
    const timer = setTimeout(() => recipientSearch && fetchRecipientCities(recipientSearch), 300);
    return () => clearTimeout(timer);
  }, [recipientSearch, fetchRecipientCities]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (pickupAddressSearch) fetchPickupAddress(pickupAddressSearch);
    }, 300);
    return () => clearTimeout(timer);
  }, [pickupAddressSearch, fetchPickupAddress]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (deliveryAddressSearch) fetchDeliveryAddress(deliveryAddressSearch);
    }, 300);
    return () => clearTimeout(timer);
  }, [deliveryAddressSearch, fetchDeliveryAddress]);

  useEffect(() => {
    if (pickupMode === "pvz" && selectedSenderCity?.code) {
      loadPickupPvz(selectedSenderCity.code);
    } else {
      setPickupPvzOptions([]);
    }
  }, [pickupMode, selectedSenderCity, loadPickupPvz]);

  useEffect(() => {
    if ((deliveryMode === "pvz" || deliveryMode === "postamat") && selectedRecipientCity?.code) {
      loadDeliveryPvz(selectedRecipientCity.code);
    } else {
      setDeliveryPvzOptions([]);
    }
  }, [deliveryMode, selectedRecipientCity, loadDeliveryPvz]);

  const handleSaveTemplate = async () => {
    try {
      const values = await templateForm.validateFields();

      if (pickupMode === "door" && !values.pickup_address_data) {
        message.error("Необходимо выбрать адрес забора из предложенного списка");
        return;
      }
      if (deliveryMode === "door" && !values.delivery_address_data) {
        message.error("Необходимо выбрать адрес доставки из предложенного списка");
        return;
      }

      setLoading(true);
      await apiRequests.integrationCdek.saveTemplate(values, query.token);
      message.success("Шаблон успешно сохранён");
    } catch (error) {
      if (error.errorFields) message.error("Проверьте обязательные поля");
      else message.error("Ошибка сохранения");
    } finally {
      setLoading(false);
    }
  };

  const handleLkLogin = async (values) => {
    setLoading(true);
    try {
      const res = await apiRequests.integrationCdek.lkLogin(values, query.token);
      setLkProfile(res.data.profile);
      message.success("Успешный вход в личный кабинет");
      handleGetIntegrationKeys();
    } catch (error) {
      console.error(error);
      const errorMsg = error.response?.data?.message || "Ошибка авторизации в ЛК СДЭК";
      message.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleLkLogout = async () => {
    setLoading(true);
    try {
      await apiRequests.integrationCdek.lkLogout(query.token);
      setLkProfile(null);
      lkForm.resetFields();
      message.info("Вы вышли из личного кабинета СДЭК");
    } catch (error) {
      console.error(error);
      message.error("Ошибка при выходе");
    } finally {
      setLoading(false);
    }
  };

  const handleGetIntegrationKeys = async () => {
    setLoading(true);
    try {
      await apiRequests.integrationCdek.getIntegrationKeys(query.token);
      message.success('Ключи интеграции получены и сохранены. Интеграция активирована.');
      setActiveTab('template');
    } catch (error) {
      console.error(error);
      const errorMsg = error.response?.data?.detail || 'Ошибка получения ключей';
      message.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal title="Интеграция с СДЭК" open={visible} onCancel={onClose} footer={[]} width={920}>
      <Spin spinning={loading}>
        <Tabs activeKey={activeTab} onChange={setActiveTab}>
          <TabPane tab="Личный кабинет" key="lk">
            {!lkProfile ? (
              <Form form={lkForm} onFinish={handleLkLogin} layout="vertical">
                <Alert
                  message="Вход в личный кабинет СДЭК"
                  description="Введите логин и пароль от вашего аккаунта на lk.cdek.ru для просмотра данных и заполнения шаблона."
                  type="info"
                  showIcon
                  style={{ marginBottom: 16 }}
                />
                <Form.Item name="login" label="Логин (номер договора)" rules={[{ required: true }]}>
                  <Input placeholder="" />
                </Form.Item>
                <Form.Item name="password" label="Пароль" rules={[{ required: true }]}>
                  <Input.Password placeholder="" />
                </Form.Item>
                <Form.Item style={{ textAlign: 'center' }}>
                  <Button type="primary" htmlType="submit" style={{ width: "185px" }} loading={loading}>
                    Войти
                  </Button>
                </Form.Item>
              </Form>
            ) : (
              <div>
                <Card title="Данные клиента СДЭК" bordered={false}>
                  <Descriptions column={1} bordered size="small">
                    <Descriptions.Item label="Контрагент UUID">{lkProfile.contragentUuid}</Descriptions.Item>
                    <Descriptions.Item label="Наименование">{lkProfile.displayName}</Descriptions.Item>
                    <Descriptions.Item label="Тип">{lkProfile.isUr ? "Юридическое лицо" : "Физическое лицо"}</Descriptions.Item>
                    <Descriptions.Item label="ИНН">{lkProfile.inn}</Descriptions.Item>
                    <Descriptions.Item label="Договор">{lkProfile.contractNumber}</Descriptions.Item>
                    <Descriptions.Item label="Телефон">{lkProfile.phones?.map(p => p.number).join(", ")}</Descriptions.Item>
                    <Descriptions.Item label="Email">{lkProfile.email}</Descriptions.Item>
                    <Descriptions.Item label="Адрес (юридический)">{lkProfile.addressLegal?.full}</Descriptions.Item>
                    <Descriptions.Item label="Адрес (фактический)">{lkProfile.addressReal?.full}</Descriptions.Item>
                  </Descriptions>
                </Card>
                <Divider />
                <Space direction="vertical" style={{ width: "100%" }}>
                  <Button onClick={handleLkLogout} block>Выйти из личного кабинета</Button>
                </Space>
              </div>
            )}
          </TabPane>

          <TabPane tab="Шаблон заказов" key="template" disabled={!lkProfile}>
            {lkProfile && (
              <Spin spinning={loadingTemplate}>
                <Form form={templateForm} layout="vertical">
                  {/* скрытые поля */}
                  <Form.Item name="order_type" hidden />
                  <Form.Item name="sender_city_data" hidden />
                  <Form.Item name="recipient_city_data" hidden />
                  <Form.Item name="pickup_address_data" hidden />
                  <Form.Item name="pickup_pvz_data" hidden />
                  <Form.Item name="delivery_address_data" hidden />
                  <Form.Item name="delivery_point_data" hidden />
                  <Form.Item name="pickup_date" hidden />
                  <Form.Item name="pickup_time_from" hidden />
                  <Form.Item name="pickup_time_to" hidden />
                  <Form.Item name="pickup_break_from" hidden />
                  <Form.Item name="pickup_break_to" hidden />

                  <Card title="Тип заказа" size="small" style={{ marginBottom: 16 }}>
                    <Segmented
                      options={[
                        { label: "Интернет-магазин", value: "im" },
                        { label: "Доставка", value: "delivery" },
                      ]}
                      value={orderType}
                      onChange={(v) => {
                        setOrderType(v);
                        templateForm.setFieldsValue({ order_type: v });
                      }}
                      block
                    />
                  </Card>

                  <Card title="Отправитель (вы)" size="small" style={{ marginBottom: 16 }}>
                    <Form.Item label="Наименование компании" name="sender_company" required>
                      <Input />
                    </Form.Item>
                    <Form.Item label="ФИО" name="sender_fio" required>
                      <Input />
                    </Form.Item>
                    <Form.Item label="Телефон" name="sender_phone" required>
                      <Input placeholder="+7 (___) ___-__-__" />
                    </Form.Item>

                    <Form.Item label="Город отправителя" required>
                      <Select
                        showSearch
                        allowClear
                        placeholder="Введите город"
                        options={senderCityOptions}
                        loading={loadingSenderCity}
                        onSearch={setSenderSearch}
                        filterOption={false}
                        value={selectedSenderCity?.full_name || undefined}
                        onSelect={(_, option) => {
                          setSelectedSenderCity(option.data);
                          templateForm.setFieldsValue({ sender_city_data: option.data });
                        }}
                      />
                    </Form.Item>

                    <Form.Item label="ИНН юр.лица" name="sender_inn">
                      <Input />
                    </Form.Item>
                  </Card>

                  {orderType === "im" && (
                    <Card title="Истинный продавец" size="small" style={{ marginBottom: 16 }}>
                      <Space direction="vertical" style={{ width: "100%" }}>
                        <Form.Item label="Номер отправления ИМ" name="im_number" required>
                          <Input />
                        </Form.Item>
                        <Form.Item label="ФИО истинного продавца" name="seller_name">
                          <Input />
                        </Form.Item>
                        <Form.Item label="Форма собственности" name="seller_ownership_form">
                          <Select options={OWNERSHIP_OPTIONS} />
                        </Form.Item>
                        <Form.Item label="ИНН истинного продавца" name="seller_inn">
                          <Input />
                        </Form.Item>
                        <Form.Item label="Телефон истинного продавца" name="seller_phone">
                          <Input placeholder="+7 (___) ___-__-__" />
                        </Form.Item>
                      </Space>
                    </Card>
                  )}

                  <Card title="Получатель" size="small" style={{ marginBottom: 16 }}>
                    <Form.Item label="Тип получателя" required>
                      <Segmented
                        options={[
                          { label: "Физическое лицо", value: "individual" },
                          { label: "Юридическое лицо", value: "legal" },
                        ]}
                        value={recipientLegal ? "legal" : "individual"}
                        onChange={(val) => {
                          const isLegal = val === "legal";
                          setRecipientLegal(isLegal);
                          templateForm.setFieldsValue({ recipient_legal: isLegal });
                        }}
                        block
                      />
                    </Form.Item>

                    {recipientLegal && (
                      <>
                        <Form.Item label="Наименование компании" name="recipient_company" required>
                          <Input />
                        </Form.Item>
                        <Form.Item label="ИНН юр. лица" name="recipient_inn">
                          <Input />
                        </Form.Item>
                      </>
                    )}

                    <Form.Item label="ФИО" name="recipient_fio" required>
                      <Input />
                    </Form.Item>
                    <Form.Item label="Телефон" name="recipient_phone" required>
                      <Input placeholder="+7 (___) ___-__-__" />
                    </Form.Item>

                    <Form.Item label="Город получателя" required>
                      <Select
                        showSearch
                        allowClear
                        placeholder="Введите город"
                        options={recipientCityOptions}
                        loading={loadingRecipientCity}
                        onSearch={setRecipientSearch}
                        filterOption={false}
                        value={selectedRecipientCity?.full_name || undefined}
                        onSelect={(_, option) => {
                          setSelectedRecipientCity(option.data);
                          templateForm.setFieldsValue({ recipient_city_data: option.data });
                        }}
                      />
                    </Form.Item>

                    <Form.Item label="Email" name="recipient_email">
                      <Input />
                    </Form.Item>
                  </Card>

                  <Card title="Способ забора и доставки" size="small" style={{ marginBottom: 16 }}>
                    <Form.Item name="pickup_mode" hidden />
                    <Form.Item name="delivery_mode" hidden />
                    <Form.Item label="Забор груза">
                      <Segmented
                        options={[
                          { label: 'Курьер заберёт по адресу склада', value: 'door' },
                          { label: 'Самостоятельно привезти в ПВЗ', value: 'pvz' },
                        ]}
                        value={pickupMode}
                        onChange={(value) => {
                          setPickupMode(value);
                          templateForm.setFieldsValue({ pickup_mode: value });
                        }}
                        block
                      />
                    </Form.Item>
                    <Form.Item label="Доставка получателю">
                      <Segmented
                        options={[
                          { label: 'Курьером по адресу', value: 'door' },
                          { label: 'В ПВЗ', value: 'pvz' },
                          { label: 'В постамат', value: 'postamat' },
                        ]}
                        value={deliveryMode}
                        onChange={(value) => {
                          setDeliveryMode(value);
                          templateForm.setFieldsValue({ delivery_mode: value });
                        }}
                        block
                      />
                    </Form.Item>
                  </Card>

                  {pickupMode === 'door' && (
                    <Card title="Забор курьером" size="small" style={{ marginBottom: 16 }}>
                      <Form.Item
                        label="Адрес"
                        name="pickup_address"
                        required
                        rules={[{ required: true, message: "Выберите адрес из списка" }]}
                      >
                        <Select
                          showSearch
                          allowClear
                          placeholder="Введите адрес"
                          options={pickupAddressOptions}
                          loading={loadingPickupAddress}
                          onSearch={setPickupAddressSearch}
                          filterOption={false}
                          notFoundContent={loadingPickupAddress ? <Spin size="small" /> : null}
                          onSelect={(value, option) => {
                            templateForm.setFieldsValue({
                              pickup_address: value,
                              pickup_address_data: option.data,
                            });
                          }}
                        />
                      </Form.Item>
                      <Space direction="horizontal" style={{ width: '60%', justifyContent: 'space-between' }}>
                        <Form.Item label="Дата и время забора" required name="pickup_date">
                          <DatePicker placeholder="04.03.2026" style={{ marginTop:"-8px"}}/>
                        </Form.Item>
                        <Form.Item name="pickup_time_from" noStyle>
                          <TimePicker placeholder="09:00" format="HH:mm" />
                        </Form.Item>
                        <span>–</span>
                        <Form.Item name="pickup_time_to" noStyle>
                          <TimePicker placeholder="18:00" format="HH:mm" />
                        </Form.Item>
                      </Space>
                      <Form.Item label="Комментарий для курьера" name="pickup_comment">
                        <Input.TextArea rows={2} placeholder="Дополнительные указания" />
                      </Form.Item>
                      <Divider style={{ margin: '12px 0' }} />
                      <Space direction="vertical" style={{ width: '100%', paddingBottom: 16 }}>
                        <Space>
                          <span>Перерыв</span>
                          <Form.Item name="pickup_break_from" noStyle>
                            <TimePicker placeholder="--:--" format="HH:mm" />
                          </Form.Item>
                          <span>–</span>
                          <Form.Item name="pickup_break_to" noStyle>
                            <TimePicker placeholder="--:--" format="HH:mm" />
                          </Form.Item>
                        </Space>
                        <Space direction="vertical" style={{marginTop: 16}}>
                          <Form.Item label="Курьеру необходима доверенность" name="pickup_need_authority" valuePropName="checked">
                            <Switch />
                          </Form.Item>
                          <Form.Item label="Курьеру необходим документ, удостоверяющий личность" name="pickup_need_passport" valuePropName="checked">
                            <Switch />
                          </Form.Item>
                        </Space>
                      </Space>
                    </Card>
                  )}

                  {pickupMode === 'pvz' && (
                    <Card title="ПВЗ отправления" size="small" style={{ marginBottom: 16 }}>
                      <Form.Item label="Пункт выдачи" required>
                        <Select
                          placeholder="Выберите ПВЗ"
                          options={pickupPvzOptions}
                          loading={loadingPickupPvz}
                          showSearch
                          optionFilterProp="label"
                          value={selectedPickupPvz?.code || undefined}
                          onSelect={(_, option) => {
                            setSelectedPickupPvz(option.data);
                            templateForm.setFieldsValue({ pickup_pvz_data: option.data });
                          }}
                        />
                      </Form.Item>
                    </Card>
                  )}

                  {deliveryMode === 'door' && (
                    <Card title="Доставка курьером" size="small" style={{ marginBottom: 16 }}>
                      <Form.Item
                        label="Адрес"
                        name="delivery_address"
                        required
                        rules={[{ required: true, message: "Выберите адрес из списка" }]}
                      >
                        <Select
                          showSearch
                          allowClear
                          placeholder="Введите адрес"
                          options={deliveryAddressOptions}
                          loading={loadingDeliveryAddress}
                          onSearch={setDeliveryAddressSearch}
                          filterOption={false}
                          notFoundContent={loadingDeliveryAddress ? <Spin size="small" /> : null}
                          onSelect={(value, option) => {
                            templateForm.setFieldsValue({
                              delivery_address: value,
                              delivery_address_data: option.data,
                            });
                          }}
                        />
                      </Form.Item>
                      <Form.Item label="Комментарий для курьера" name="delivery_comment">
                        <Input.TextArea rows={2} placeholder="Дополнительные указания"/>
                      </Form.Item>
                    </Card>
                  )}

                  {(deliveryMode === 'pvz' || deliveryMode === 'postamat') && (
                    <Card
                      title={deliveryMode === 'pvz' ? "ПВЗ получения" : "Постамат получения"}
                      size="small"
                      style={{ marginBottom: 16 }}
                    >
                      <Form.Item label="Пункт выдачи" required>
                        <Select
                          placeholder="Выберите пункт"
                          options={deliveryPvzOptions}
                          loading={loadingDeliveryPvz}
                          showSearch
                          optionFilterProp="label"
                          value={selectedDeliveryPoint?.code || undefined}
                          onSelect={(_, option) => {
                            setSelectedDeliveryPoint(option.data);
                            templateForm.setFieldsValue({ delivery_point_data: option.data });
                          }}
                        />
                      </Form.Item>
                    </Card>
                  )}

                  <Button
                    type="primary"
                    icon={<SaveOutlined />}
                    onClick={handleSaveTemplate}
                    block
                    size="large"
                    loading={loading}
                  >
                    Сохранить шаблон
                  </Button>
                </Form>
              </Spin>
            )}
          </TabPane>
        </Tabs>
      </Spin>
    </Modal>
  );
};

export default CdekModal;